import { useMemo, type ReactNode } from "react";
import { Belegleiste, Spur } from "./Spur";
import { Markierung } from "./Markierung";
import { faltEreignisse, planAusAnker } from "./verlauf";
import { euro, statusLabel, uebersetzeFehler } from "./texte";
import type { Beleg, Knoten, WerkzeugEintrag, Zug } from "./typen";

/**
 * One exchange, rendered.
 *
 * The order is the order the work happened in: the question, then the trace of what
 * was looked up, then the answer, then what it cost. A reader who trusts the assistant
 * stops after the answer; a reader checking a figure walks back up the trace to the
 * record. Both are one column, because two columns at 380 px is one column with a
 * scrollbar.
 */

interface ZugblockProps {
  zug: Zug;
  katalog: Map<string, WerkzeugEintrag>;
  /**
   * Renders the approval card. `erledigt` says the run has moved past this
   * checkpoint, which is how a reloaded transcript knows not to offer the buttons
   * again — the open-approvals list no longer contains a request that was decided,
   * so its absence alone cannot distinguish "decided" from "not loaded yet".
   */
  freigabe: (freigabeId: string | null, titel: string, erledigt: boolean) => ReactNode;
}

export function Zugblock({ zug, katalog, freigabe }: ZugblockProps) {
  const knoten = useMemo(
    () => faltEreignisse(zug.ereignisse, planAusAnker(zug.lauf?.anker)),
    [zug.ereignisse, zug.lauf],
  );
  const laufend = zug.status === "laeuft" || zug.status === "geplant";

  const stroemend = Object.keys(zug.teiltexte)
    .map(Number)
    .sort((a, b) => a - b)
    .map((block) => zug.teiltexte[block] ?? "")
    .join("")
    .trim();

  const belege = useMemo(() => sammleBelege(knoten), [knoten]);

  // A failed run's closing text is usually the kernel restating the failure. Showing
  // it above the failure block would print the same fault twice, once in the
  // provider's English and once in German.
  const antwort = zug.antwortMd && !wiederholtDenFehler(zug) ? zug.antwortMd : null;

  return (
    <article className="flex flex-col gap-[6px]">
      <Frage text={zug.frage} kontext={zug.kontextLabel} />

      <div className="flex flex-col gap-[6px]">
        <Etikett>Assistent</Etikett>

        <Spur
          knoten={knoten}
          katalog={katalog}
          freigabe={(freigabeId, titel) =>
            freigabe(
              freigabeId,
              titel,
              zug.lauf ? zug.lauf.offeneFreigabeId !== freigabeId : false,
            )
          }
        />

        {stroemend ? (
          <p
            className="text-[length:var(--text-sm)] whitespace-pre-wrap"
            style={{ color: "var(--text-primary)", lineHeight: "var(--leading-text)" }}
          >
            {stroemend}
          </p>
        ) : null}

        {laufend && !stroemend && knoten.length === 0 ? (
          <p className="text-[length:var(--text-xs)]" style={{ color: "var(--text-muted)" }}>
            Der Assistent liest den Bestand …
          </p>
        ) : null}

        {antwort ? <Markierung text={antwort} /> : null}

        {antwort && belege.length > 0 ? <Belegleiste belege={belege} /> : null}

        {zug.status === "fehlgeschlagen" ? <Fehlerblock zug={zug} /> : null}

        {zug.status === "wartet_auf_freigabe" && knoten.every((k) => k.art !== "freigabe") ? (
          <p className="text-[length:var(--text-xs)]" style={{ color: "var(--status-warn-tx)" }}>
            Der Lauf wartet auf eine Freigabe.
          </p>
        ) : null}

        <Fusszeile zug={zug} />
      </div>
    </article>
  );
}

function Frage({ text, kontext }: { text: string; kontext: string | null }) {
  return (
    <div
      className="flex flex-col gap-[2px] border-l-2 pl-[8px]"
      style={{ borderColor: "var(--line-strong)" }}
    >
      <Etikett>Sie</Etikett>
      <p
        className="text-[length:var(--text-sm)] whitespace-pre-wrap"
        style={{ color: "var(--text-primary)", lineHeight: "var(--leading-snug)" }}
      >
        {text}
      </p>
      {kontext ? (
        <p className="text-[length:var(--text-3xs)]" style={{ color: "var(--text-muted)" }}>
          gefragt in {kontext}
        </p>
      ) : null}
    </div>
  );
}

/**
 * A failed run, said out loud.
 *
 * The kernel records the provider's own words, which for a model call are English and
 * often about billing rather than about the question. Naming the cause in German and
 * keeping the original underneath means a Verwalter learns whether to try again, call
 * the office, or stop trusting the answer.
 */
function Fehlerblock({ zug }: { zug: Zug }) {
  const fehler = uebersetzeFehler(zug.fehlerCode, zug.fehlerText);

  return (
    <div
      className="border border-l-2 px-[8px] py-[6px] flex flex-col gap-[4px] rounded-[var(--radius-2)]"
      style={{
        borderColor: "var(--line-hairline)",
        borderLeftColor: "var(--status-bad-bar)",
        background: "var(--status-bad-bg)",
      }}
      role="alert"
    >
      <p
        className="text-[length:var(--text-2xs)]"
        style={{
          color: "var(--status-bad-tx)",
          textTransform: "var(--label-transform)" as never,
          letterSpacing: "var(--tracking-caps)",
          fontWeight: "var(--weight-medium)",
        }}
      >
        Fehlgeschlagen
      </p>
      <p
        className="text-[length:var(--text-sm)]"
        style={{ color: "var(--text-primary)", lineHeight: "var(--leading-snug)" }}
      >
        {fehler.satz}
      </p>
      {fehler.roh ? (
        <details>
          <summary
            className="text-[length:var(--text-2xs)] cursor-pointer"
            style={{ color: "var(--text-muted)" }}
          >
            Technische Meldung
          </summary>
          <pre
            className="mono text-[length:var(--text-2xs)] whitespace-pre-wrap break-words mt-[2px]"
            style={{ color: "var(--text-secondary)" }}
          >
            {fehler.roh}
          </pre>
        </details>
      ) : null}
    </div>
  );
}

/**
 * Cost, quietly.
 *
 * The organisation pays per token, so the number belongs on screen; nobody makes a
 * decision because of it, so it belongs at the bottom in the smallest legal size. It
 * is shown against the run's budget rather than alone, because a figure without its
 * ceiling tells you nothing about whether to worry.
 */
function Fusszeile({ zug }: { zug: Zug }) {
  const lauf = zug.lauf;
  if (!lauf || zug.status === "laeuft" || zug.status === "geplant") return null;

  return (
    <p
      className="text-[length:var(--text-3xs)] flex flex-wrap items-baseline gap-[6px]"
      style={{ color: "var(--text-muted)" }}
    >
      <span>{statusLabel(zug.status)}</span>
      <span className="mono">{lauf.modell}</span>
      <span className="num">
        {lauf.verbrauchteZuege} von {lauf.budgetZuege} Zügen
      </span>
      <span className="num">
        {euro(lauf.verbrauchCent)} von {euro(lauf.budgetCent)}
      </span>
    </p>
  );
}

export function Etikett({ children }: { children: string }) {
  return (
    <span
      className="text-[length:var(--text-3xs)]"
      style={{
        color: "var(--text-caps)",
        textTransform: "var(--label-transform)" as never,
        letterSpacing: "var(--tracking-caps)",
        fontWeight: "var(--weight-medium)",
      }}
    >
      {children}
    </span>
  );
}

/** True when the closing text is just the stored failure in other words. */
function wiederholtDenFehler(zug: Zug): boolean {
  if (zug.status !== "fehlgeschlagen" || !zug.antwortMd || !zug.fehlerText) return false;
  const probe = zug.fehlerText.replace(/\s+/g, " ").trim().slice(0, 48);
  return probe.length > 12 && zug.antwortMd.replace(/\s+/g, " ").includes(probe);
}

/** Every record the run touched, de-duplicated, for the answer's citation list. */
function sammleBelege(knoten: Knoten[]): Beleg[] {
  const gesehen = new Map<string, Beleg>();

  const gehe = (liste: Knoten[]): void => {
    for (const k of liste) {
      if (k.art === "werkzeug") {
        for (const beleg of k.belege) gesehen.set(beleg.href, beleg);
      } else if (k.art === "unteragent") {
        gehe(k.kinder);
      }
    }
  };

  gehe(knoten);
  return [...gesehen.values()].slice(0, 12);
}
